import React, { useState, ReactText, useEffect } from "react";
import {
	Opportunity,
	OpportunityRequest
} from "@/Contracts/Opportunity";
import { getOpportunities } from "@/Services/Opportunity";
import { IOpportunitySelectProps, OpportunityMap } from "./interface";
import ZDynamicSelect from "@molecules/ZDynamicSelect";
import { useFeatureFlag } from "@/Hooks";
import { GlobalSearchEntityType } from "@/Contracts/Others/GlobalSearch";
import { GlobalSearchRequest } from "@/Contracts/Others/GlobalSearch";
import { getGlobalSearch } from "@/Utils/Service/GlobalSearch";

const ZOpportunitySelect: React.FC<IOpportunitySelectProps> = props => {
	const {
		defaultOptions,
		childHydrationParams,
		selectedIds,
		limit,
		opportunityFilters,
		onChange,
		filterOpportunities,
		useGlobalSearch,
		...rest
	} = props;

	const [searchResult, setSearchResult] = useState<OpportunityMap>({});

	const useAtlasSearchFlag = useFeatureFlag("use_atlas_search");

	useEffect(() => {
		if (defaultOptions?.length) {
			const defaultOptionsMap =
				defaultOptions?.reduce((acc, currOption) => {
					acc["" + currOption.id] = currOption;
					return acc;
				}, {} as OpportunityMap) ?? {};
			setSearchResult(defaultOptionsMap);
		} else {
			setSearchResult({});
		}
	}, [defaultOptions]);

	const searchOpportunities = async (value: string) => {
		try {
			if (useGlobalSearch) {
				const globalSearchRequest = new GlobalSearchRequest(
					value,
					[GlobalSearchEntityType.Opportunity],
					limit ?? 50,
					null,
					useAtlasSearchFlag,
					childHydrationParams
				);
				const response = await getGlobalSearch(globalSearchRequest);
				setSearchResult(
					response.data
						.map(item => item.obj as Opportunity)
						.reduce((acc, currOption) => {
							acc["" + currOption.id] = currOption;
							return acc;
						}, {} as OpportunityMap)
				);
			} else {
				const opportunityRequest = new OpportunityRequest(
					opportunityFilters,
					value,
					childHydrationParams,
					limit ?? 50,
					selectedIds,
					true
				);
				const response = await getOpportunities(opportunityRequest);

				const newSearchResult: OpportunityMap = {};
				response.data.forEach(currOption => {
					newSearchResult["" + currOption.id] = currOption;
				});

				setSearchResult(newSearchResult);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const opportunities = Object.values(searchResult);

	const filteredOpportunities = filterOpportunities
		? filterOpportunities(opportunities)
		: opportunities;

	const handleChange = (id: ReactText) => {
		onChange?.(id, searchResult[id]);
	};

	const formattedOptions = filteredOpportunities.map(opportunity => ({
		name: opportunity.name,
		value: opportunity.id
	}));

	return (
		<ZDynamicSelect
			defaultOptions={formattedOptions}
			fetchData={searchOpportunities}
			options={formattedOptions}
			onChange={handleChange}
			{...rest}
		/>
	);
};

export default ZOpportunitySelect;
